import React from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import EpicPanelApp from '@atlassian/jira-software-backlog-panel/src/async';
import {
	EXPERIENCE_TRACKER_PROVIDER_SOURCE,
	ANALYTICS_SOURCE_EPIC_PANEL,
} from '@atlassian/jira-software-backlog-panel/src/common/constants';
import { PanelDataProvider } from '@atlassian/jira-software-backlog-panel/src/context/use-panel-data';
import type { IssueParent } from '../../../model/issue-parent/types';
import type { CardId, PartialCard } from '../../../model/types';
import { useBacklogSelector } from '../../../state';
import { getBaseLevelCardTypes } from '../../../state/selectors/card-type';
import { selectedPartialCardsWithTypeHierarchyLevelSelector } from '../../../state/selectors/cards';
import { getSourceDroppableType } from '../../../state/selectors/drag';
import { getFirstLevelHierarchyName } from '../../../state/selectors/issue-hierarchy';
import { canCreateIssues, canEditIssue } from '../../../state/selectors/permissions';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities';
import { useIsEpicPanelOpen } from '../../../state/state-hooks/use-is-epic-panel-open';
import SLAExperienceTracker from '../../sla-experience-tracker';

interface Props {
	boardId: number;
	selectedIssues: PartialCard[];
	issueHierarchyLevel: number;
	epicLinkFieldId?: string;
	onEpicPanelClose: () => void;
	onCardDetailClick: (cardId: CardId) => void;
	onEpicCreate: (issueParents: IssueParent[]) => void;
	onEpicAssign: (
		cards: { id: number }[],
		parentId: number | null,
		requestPromise: Promise<undefined>,
	) => void;
	isCompanyManaged: boolean;
}

export const EpicPanel = ({
	boardId,
	selectedIssues,
	issueHierarchyLevel,
	epicLinkFieldId,
	onEpicPanelClose,
	onCardDetailClick,
	onEpicCreate,
	onEpicAssign,
	isCompanyManaged,
}: Props) => {
	const isEpicPanelOpen = useIsEpicPanelOpen();
	const baseLevelCardTypes = useBacklogSelector(getBaseLevelCardTypes);
	const selectedIssuesWithTypeHierarchyLevel = useBacklogSelector(
		selectedPartialCardsWithTypeHierarchyLevelSelector,
	);

	const canCreate = useBacklogSelector(canCreateIssues);
	const canEdit = useBacklogSelector(canEditIssue);

	const firstLevelHierarchyName = useBacklogSelector(getFirstLevelHierarchyName);
	const isCmpManaged = useIsCMPBacklog();
	const sourceDroppableType = useBacklogSelector(getSourceDroppableType);
	return isEpicPanelOpen ? (
		<SLAExperienceTracker
			experienceTarget="epic panel"
			experienceTargetId={`${boardId}`}
			experienceSource={EXPERIENCE_TRACKER_PROVIDER_SOURCE}
		>
			<ContextualAnalyticsData
				attributes={{
					analyticsSource: ANALYTICS_SOURCE_EPIC_PANEL,
					issueHierarchyLevel,
				}}
			>
				<PanelWrapper>
					<PanelDataProvider
						sourceDroppableType={sourceDroppableType}
						boardId={boardId}
						isCompanyManaged={isCmpManaged}
					>
						<EpicPanelApp
							selectedIssues={
								isCompanyManaged ? selectedIssuesWithTypeHierarchyLevel : selectedIssues
							}
							defaultIssueCreateIssueTypeId={
								ff('jsw-create-std-issue-using-epic-panel-create-issue-_4d68w')
									? baseLevelCardTypes[0]?.id.toString()
									: undefined
							}
							onClose={onEpicPanelClose}
							onCardDetailClick={onCardDetailClick}
							onEpicCreate={onEpicCreate}
							onEpicAssign={onEpicAssign}
							isCompanyManaged={isCompanyManaged}
							epicLinkFieldId={epicLinkFieldId}
							hierarchyName={firstLevelHierarchyName}
							canCreate={canCreate}
							canEdit={canEdit}
						/>
					</PanelDataProvider>
				</PanelWrapper>
			</ContextualAnalyticsData>
		</SLAExperienceTracker>
	) : null;
};

export const EpicPanelWithErrorBoundary = (props: Props) => (
	<JSErrorBoundary
		id="backlogContentEpicPanel"
		teamName="a4t-tanuki"
		packageName="jiraSoftwareBacklog"
		fallback="flag"
	>
		<EpicPanel {...props} />
	</JSErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PanelWrapper = styled.div({
	display: 'flex',
	padding: `0 ${token('space.100', '8px')} 0 0`,
});
